/**
 * 逻辑流编辑器 - 右侧节点属性配置面板
 */

import React, {useState} from 'react';
import {Button, Divider, Input, Select, Tag, Tooltip, Typography} from 'antd';
import {DeleteOutlined, EditOutlined, PlusOutlined, UnorderedListOutlined} from '@ant-design/icons';
import type {
    ConditionRule,
    IndicatorMode,
    IndicatorNodeConfig,
    IndicatorParam,
    LogicFlowEdge,
    LogicFlowNode
} from '../../../types/logicFlow';
import {INDICATOR_FUNCTION_LIBRARY} from '../../../types/logicFlow';
import {buildIndicatorFunctionCall, generateNodeCode} from '../../../utils/logicFlowCodegen';

const {Text} = Typography;

const COMPARISON_OPERATORS = [
    {value: '>', label: '>'},
    {value: '<', label: '<'},
    {value: '>=', label: '>='},
    {value: '<=', label: '<='},
    {value: '==', label: '=='},
    {value: '!=', label: '!='},
    {value: 'cross_above', label: '上穿'},
    {value: 'cross_below', label: '下穿'},
];

const ACTION_OPTIONS = [
    {value: 'enter_long', label: '做多'},
    {value: 'enter_short', label: '做空'},
    {value: 'exit_long', label: '平多'},
    {value: 'exit_short', label: '平空'},
    {value: 'none', label: '无（组合条件）'},
];

interface Props {
    node: LogicFlowNode | undefined;
    onUpdateConfig: (id: string, config: Record<string, unknown>) => void;
    onUpdateLabel: (id: string, label: string) => void;
    onRemoveNode: (id: string) => void;
    onAddEdge?: (edge: LogicFlowEdge) => void;
    onRemoveEdge?: (edgeId: string) => void;
    graph?: { nodes: LogicFlowNode[]; edges: LogicFlowEdge[] };
}

const NodeConfigPanel: React.FC<Props> = ({
                                              node,
                                              onUpdateConfig,
                                              onUpdateLabel,
                                              onRemoveNode,
                                              onAddEdge,
                                              onRemoveEdge,
                                              graph
                                          }) => {
    const [customLeftOperands, setCustomLeftOperands] = useState<Set<number>>(new Set());
    const [customRightOperands, setCustomRightOperands] = useState<Set<number>>(new Set());

    const toggleCustomLeft = (idx: number) => {
        const next = new Set(customLeftOperands);
        if (next.has(idx)) {
            next.delete(idx);
        } else {
            next.add(idx);
        }
        setCustomLeftOperands(next);
    };

    const toggleCustomRight = (idx: number) => {
        const next = new Set(customRightOperands);
        if (next.has(idx)) {
            next.delete(idx);
        } else {
            next.add(idx);
        }
        setCustomRightOperands(next);
    };

    const nodeCode = (!node || !graph) ? '' : generateNodeCode(node, {nodes: graph.nodes, edges: graph.edges});

    if (!node) {
        return (
            <div style={containerStyle}>
                <div style={emptyStyle}>
                    <Text type="secondary">点击画布中的节点进行编辑</Text>
                </div>
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            <div style={titleStyle}>
                <Text strong style={{color: 'var(--pt-text-primary)'}}>节点配置</Text>
                <Tag color="purple" style={{fontSize: '11px'}}>{node.type}</Tag>
            </div>

            <div style={fieldStyle}>
                <Text type="secondary" style={{fontSize: '12px'}}>名称</Text>
                <Input
                    size="small"
                    value={node.label}
                    onChange={e => onUpdateLabel(node.id, e.target.value)}
                    style={solidInputStyle}
                />
            </div>

            {nodeCode && (
                <>
                    <Divider style={{margin: '12px 0', borderColor: 'rgba(255,255,255,0.08)'}}/>
                    <Text type="secondary" style={{fontSize: '12px', marginBottom: '4px'}}>代码预览</Text>
                    <pre style={codeStyle}>{nodeCode}</pre>
                </>
            )}

            <Divider style={{margin: '12px 0', borderColor: 'rgba(255,255,255,0.08)'}}/>

            {renderTypeSpecificConfig(node, onUpdateConfig, onAddEdge, onRemoveEdge, graph, customLeftOperands, toggleCustomLeft, customRightOperands, toggleCustomRight)}

            <Divider style={{margin: '12px 0', borderColor: 'rgba(255,255,255,0.08)'}}/>

            <Tooltip title="删除节点">
                <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined/>}
                    onClick={() => onRemoveNode(node.id)}
                    block
                />
            </Tooltip>
        </div>
    );
};

function renderTypeSpecificConfig(
    node: LogicFlowNode,
    onUpdate: (id: string, config: Record<string, unknown>) => void,
    onAddEdge?: (edge: LogicFlowEdge) => void,
    onRemoveEdge?: (edgeId: string) => void,
    graph?: { nodes: LogicFlowNode[]; edges: LogicFlowEdge[] },
    customLeftOperands?: Set<number>,
    toggleCustomLeft?: (idx: number) => void,
    customRightOperands?: Set<number>,
    toggleCustomRight?: (idx: number) => void,
): React.ReactNode {
    switch (node.type) {
        case 'INDICATOR':
            return renderIndicatorConfig(node, onUpdate, onAddEdge, onRemoveEdge, graph);
        case 'RULE':
            return renderRuleConfig(node, onUpdate, graph, customLeftOperands, toggleCustomLeft, customRightOperands, toggleCustomRight);
        default:
            return null;
    }
}

function resolveIndicatorMode(config: IndicatorNodeConfig): IndicatorMode {
    // CUSTOM 函数永远视为自定义模式，无论 config.mode 取值
    if ('CUSTOM' === config.function) {
        return 'custom';
    }
    if (config.mode) {
        if ('raw' === config.mode) {
            // raw + 有效标准函数 + 有参数 → 标准模式
            if (config.function && Array.isArray(config.params) && config.params.length > 0) {
                return 'standard';
            }
        }
        return config.mode;
    }
    if (config.customExpression) {
        return 'custom';
    }
    if (config.rawExpression) {
        return 'raw';
    }
    return 'standard';
}

function findLibraryEntry(expression: string | undefined) {
    return INDICATOR_FUNCTION_LIBRARY.find(e => e.expression === expression);
}

function cloneParams(params: IndicatorParam[] | undefined): IndicatorParam[] {
    return (params || []).map(p => ({...p}));
}

function hasParams(config: IndicatorNodeConfig): boolean {
    return Array.isArray(config.params) && config.params.length > 0;
}

function getCustomInitialExpression(config: IndicatorNodeConfig): string {
    if (config.customExpression) {
        return config.customExpression;
    }
    if (config.function && 'CUSTOM' !== config.function) {
        return buildIndicatorFunctionCall(config);
    }
    if (config.rawExpression) {
        return config.rawExpression;
    }
    if (config.outputKey) {
        return `${config.function}(... )['${config.outputKey}']`;
    }
    return '';
}

function renderIndicatorConfig(
    node: LogicFlowNode,
    onUpdate: (id: string, config: Record<string, unknown>) => void,
    onAddEdge?: (edge: LogicFlowEdge) => void,
    onRemoveEdge?: (edgeId: string) => void,
    graph?: { nodes: LogicFlowNode[]; edges: LogicFlowEdge[] }
): React.ReactNode {
    const config = node.config as IndicatorNodeConfig;
    const mode = resolveIndicatorMode(config);
    const isCustom = 'custom' === mode;

    // 收集所有可用上游 Dict 节点 + 当前连接的上游
    const upstreamDictNodes = graph ? graph.nodes.filter(n =>
        'INDICATOR' === n.type && 'Dict' === ((n.config as IndicatorNodeConfig).outputType || 'column')
    ) : [];
    // 当前上游 Dict nodeId
    const currentUpstreamNodeId = graph ? graph.edges
            .filter(e => e.target === node.id)
            .map(e => e.source)
            .find(srcId => upstreamDictNodes.some(n => n.id === srcId))
        : undefined;
    // 上游 Dict 候选项（仅保留实际通过边连接的节点）
    const upstreamDictOptions = upstreamDictNodes
        .filter(n => n.id !== node.id && n.id === currentUpstreamNodeId)
        .map(n => ({
            value: n.id,
            label: (n.config as IndicatorNodeConfig).indicatorName,
        }));
    const currentUpstreamNode = currentUpstreamNodeId
        ? upstreamDictNodes.find(n => n.id === currentUpstreamNodeId) : undefined;
    const upstreamFunction = currentUpstreamNode
        ? (currentUpstreamNode.config as IndicatorNodeConfig).function : undefined;
    const libEntry = findLibraryEntry(upstreamFunction);
    const upstreamOutputKeys: string[] | undefined =
        libEntry && libEntry.outputKeys && libEntry.outputKeys.length > 0
            ? libEntry.outputKeys : undefined;

    // 切换上游 Dict 节点
    const handleUpstreamChange = (newUpstreamId: string) => {
        if (!graph || !onAddEdge || !onRemoveEdge) return;
        // 删除旧边
        if (currentUpstreamNodeId) {
            const oldEdge = graph.edges.find(
                e => e.source === currentUpstreamNodeId && e.target === node.id
            );
            if (oldEdge) onRemoveEdge(oldEdge.id);
        }
        // 添加新边
        const newEdge: LogicFlowEdge = {
            id: `e_upstream_${newUpstreamId}_${node.id}_${Date.now()}`,
            source: newUpstreamId,
            target: node.id,
        };
        onAddEdge(newEdge);
        // 查找新上游的 outputKeys 并自动设置第一个
        const newUpNode = upstreamDictNodes.find(n => n.id === newUpstreamId);
        if (newUpNode) {
            const newFunc = (newUpNode.config as IndicatorNodeConfig).function;
            const newLibEntry = findLibraryEntry(newFunc);
            if (newLibEntry && newLibEntry.outputKeys && newLibEntry.outputKeys.length > 0) {
                onUpdate(node.id, {outputKey: newLibEntry.outputKeys[0]});
            }
        }
    };

    const toggleMode = () => {
        if (isCustom) {
            const libEntry = findLibraryEntry(config.function);
            if (libEntry) {
                // 标准库函数：按库条目正常更新
                onUpdate(node.id, {
                    mode: 'standard',
                    function: libEntry.expression,
                    params: hasParams(config) ? cloneParams(config.params) : cloneParams(libEntry.params),
                    customExpression: undefined,
                    rawExpression: config.customExpression || config.rawExpression,
                });
            } else {
                // 非标准库函数（如 CUSTOM）：仅更新模式，保留现有 function/params
                onUpdate(node.id, {
                    mode: 'standard',
                    customExpression: undefined,
                    rawExpression: config.customExpression || config.rawExpression,
                });
            }
        } else {
            // 切换自定义模式：保留 rawExpression 作为初始值
            onUpdate(node.id, {
                mode: 'custom',
                customExpression: getCustomInitialExpression(config),
            });
        }
    };

    const functionOptions = INDICATOR_FUNCTION_LIBRARY.map(e => ({
        value: e.expression,
        label: `${e.name} (${e.expression})`,
    }));

    const currentOutputType = config.outputType || 'column';

    // 当前节点是否真的有上游 INDICATOR 边（上游为 Dict 节点）
    const hasUpstreamEdge = !!currentUpstreamNodeId;

    // 是否显示上游来源 UI：只有 column 节点 + 实际存在上游 INDICATOR 边时
    const showUpstreamUI = hasUpstreamEdge && 'column' === currentOutputType;

    return (
        <div>
            <div style={fieldStyle}>
                <Text type="secondary" style={{fontSize: '12px'}}>
                    {'Dict' === currentOutputType ? '变量名' : '指标列名'}
                </Text>
                <Input
                    size="small"
                    value={config.indicatorName || ''}
                    onChange={e => onUpdate(node.id, {indicatorName: e.target.value})}
                    placeholder={'Dict' === currentOutputType ? '如 bollinger' : '如 rsi_14'}
                    style={solidInputStyle}
                />
            </div>

            <div style={fieldStyle}>
                <Text type="secondary" style={{fontSize: '12px'}}>输出类型</Text>
                <Select
                    size="small"
                    style={{width: '100%', ...solidInputStyle}}
                    value={currentOutputType}
                    onChange={v => onUpdate(node.id, {outputType: v})}
                    options={[
                        {value: 'column', label: 'column — 写入 dataframe 列'},
                        {value: 'Dict', label: 'Dict — 中间变量赋值'},
                        {value: 'List', label: 'List — 列表结果'},
                        {value: 'DataFrame', label: 'DataFrame — 数据帧结果'},
                    ]}
                />
            </div>

            {showUpstreamUI && !isCustom && (
                <div style={{
                    ...fieldStyle,
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid rgba(139,92,246,0.18)',
                    background: 'rgba(139,92,246,0.06)',
                    gap: '8px',
                }}>
                    <div style={fieldStyle}>
                        <Text type="secondary" style={{fontSize: '12px'}}>上游来源</Text>
                        <Select
                            size="small"
                            style={{width: '100%', ...solidInputStyle}}
                            value={currentUpstreamNodeId}
                            onChange={handleUpstreamChange}
                            placeholder="选择上游 Dict 节点..."
                            options={upstreamDictOptions}
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        />
                        {upstreamFunction ? (
                            <Tag style={{fontSize: '10px', margin: 0, alignSelf: 'flex-start'}}>{upstreamFunction}</Tag>
                        ) : null}
                    </div>
                    <div style={fieldStyle}>
                        <Text type="secondary" style={{fontSize: '12px'}}>取键</Text>
                        {upstreamOutputKeys ? (
                            <Select
                                size="small"
                                style={{width: '100%', ...solidInputStyle}}
                                value={config.outputKey || undefined}
                                onChange={v => onUpdate(node.id, {outputKey: v})}
                                placeholder="选择键..."
                                options={upstreamOutputKeys.map(k => ({value: k, label: k}))}
                                showSearch
                            />
                        ) : (
                            <Input
                                size="small"
                                value={config.outputKey || ''}
                                onChange={e => onUpdate(node.id, {outputKey: e.target.value})}
                                placeholder="如 lower"
                                style={solidInputStyle}
                            />
                        )}
                    </div>
                </div>
            )}
            {(!showUpstreamUI || isCustom) && (
                <>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'}}>
                        <Text type="secondary" style={{fontSize: '12px'}}>模式</Text>
                        <Tooltip title={isCustom ? '切换标准模式' : '切换自定义模式'}>
                            <Button size="small" type="text" onClick={toggleMode}
                                    icon={isCustom ? <UnorderedListOutlined/> : <EditOutlined/>}/>
                        </Tooltip>
                    </div>

                    {isCustom ? (
                        <div style={fieldStyle}>
                            <Text type="secondary" style={{fontSize: '12px'}}>自定义表达式</Text>
                            <Input.TextArea
                                size="small"
                                rows={3}
                                value={config.customExpression || config.rawExpression || ''}
                                onChange={e => onUpdate(node.id, {mode: 'custom', customExpression: e.target.value})}
                                placeholder={"如 0.1 * (dataframe['rsi'] - 50)"}
                                style={{...solidInputStyle, fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px'}}
                            />
                        </div>
                    ) : (
                        <>
                            <div style={fieldStyle}>
                                <Text type="secondary" style={{fontSize: '12px'}}>函数</Text>
                                <Select
                                    size="small"
                                    style={{width: '100%', ...solidInputStyle}}
                                    value={config.function || 'ta.SMA'}
                                    onChange={v => {
                                        const libEntry = INDICATOR_FUNCTION_LIBRARY.find(e => e.expression === v);
                                        const update: Record<string, unknown> = {
                                            mode: 'standard',
                                            function: v,
                                            customExpression: undefined,
                                            rawDependencies: undefined,
                                        };
                                        if (libEntry) {
                                            update.params = libEntry.params.map(p => ({...p}));
                                        }
                                        onUpdate(node.id, update);
                                    }}
                                    options={functionOptions}
                                />
                            </div>
                            <div style={fieldStyle}>
                                <Text type="secondary" style={{fontSize: '12px'}}>参数</Text>
                                {(config.params || []).map((param: IndicatorParam, i: number) => (
                                    <div key={i} style={{display: 'flex', gap: '6px', alignItems: 'center'}}>
                                        <Text type="secondary"
                                              style={{fontSize: '11px', minWidth: '90px', flexShrink: 0}}>
                                            {param.name}
                                        </Text>
                                        {'column' === param.type ? (
                                            <Input size="small" style={{flex: 1, ...solidInputStyle}}
                                                   value={param.value}
                                                   onChange={e => {
                                                       const newParams = (config.params || []).map((p: IndicatorParam, j: number) =>
                                                           j === i ? {...p, value: e.target.value} : p
                                                       );
                                                       onUpdate(node.id, {params: newParams});
                                                   }}
                                                   placeholder="如 close"/>
                                        ) : (
                                            <Input size="small" style={{flex: 1, ...solidInputStyle}}
                                                   value={param.value}
                                                   type={'number' === param.type ? 'number' : 'text'}
                                                   onChange={e => {
                                                       const newParams = (config.params || []).map((p: IndicatorParam, j: number) =>
                                                           j === i ? {...p, value: e.target.value} : p
                                                       );
                                                       onUpdate(node.id, {params: newParams});
                                                   }}/>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

/** 收集指定 RULE 节点上游的可用列名（OHLCV + 上游 INDICATOR 的 indicatorName） */
function getAvailableColumns(
    nodeId: string,
    graph: { nodes: LogicFlowNode[]; edges: LogicFlowEdge[] } | undefined
): { value: string; label: string }[] {
    const baseColumns = ['close', 'open', 'high', 'low', 'volume'].map(c => ({
        value: `dataframe['${c}']`,
        label: c,
    }));

    if (!graph) return baseColumns;

    // 构建反向邻接表
    const revAdj = new Map<string, string[]>();
    for (const n of graph.nodes) revAdj.set(n.id, []);
    for (const e of graph.edges) {
        const neighbors = revAdj.get(e.target);
        if (neighbors) neighbors.push(e.source);
    }

    // BFS 收集上游 INDICATOR 节点的 indicatorName（含 Dict 类型）
    const upstreamIndicators: { name: string; outputType: string }[] = [];
    const visited = new Set<string>();
    const queue = [nodeId];
    while (queue.length > 0) {
        const current = queue.shift()!;
        const upstream = revAdj.get(current) || [];
        for (const upId of upstream) {
            if (visited.has(upId)) continue;
            visited.add(upId);
            const upNode = graph.nodes.find(n => n.id === upId);
            if (upNode && 'INDICATOR' === upNode.type) {
                const ic = upNode.config as any;
                if (ic.indicatorName) {
                    upstreamIndicators.push({name: ic.indicatorName, outputType: ic.outputType || 'column'});
                }
            }
            queue.push(upId);
        }
    }

    const indicatorColumns = upstreamIndicators.map(({name, outputType}) => {
        // Dict 节点：引用为裸变量名（如 bollinger）；column 节点：引用为 dataframe['xxx']
        if ('Dict' === outputType) {
            return {value: name, label: `var:${name}`};
        }
        return {value: `dataframe['${name}']`, label: name};
    });

    return [...baseColumns, ...indicatorColumns];
}

function renderOperandCell(
    nodeId: string,
    index: number,
    value: string,
    graph: { nodes: LogicFlowNode[]; edges: LogicFlowEdge[] } | undefined,
    customSet: Set<number> | undefined,
    toggleCustom: ((idx: number) => void) | undefined,
    onChange: (value: string) => void,
): React.ReactNode {
    const availableColumns = graph ? getAvailableColumns(nodeId, graph) : [];
    const isCustom = (customSet?.has(index) ?? false) || (availableColumns.length > 0 && !availableColumns.some(c => c.value === value) && '' !== value);

    if (isCustom || availableColumns.length === 0) {
        return (
            <div style={{flex: 1, minWidth: '80px', display: 'flex', gap: '2px'}}>
                <Input size="small" style={{flex: 1, ...solidInputStyle}}
                       value={value}
                       onChange={e => onChange(e.target.value)}
                       placeholder="如 dataframe['rsi']"/>
                {toggleCustom && availableColumns.length > 0 && (
                    <Button size="small" type="link" style={{padding: '0 2px', fontSize: '12px'}}
                            onClick={() => toggleCustom(index)} title="从列表选择">
                        <UnorderedListOutlined/>
                    </Button>
                )}
            </div>
        );
    }

    return (
        <div style={{flex: 1, minWidth: '80px', display: 'flex', gap: '2px'}}>
            <Select size="small" style={{flex: 1, ...solidInputStyle}}
                    value={value || undefined}
                    onChange={v => onChange(v)}
                    options={availableColumns}
                    showSearch
                    placeholder="选择列..."/>
            {toggleCustom && (
                <Button size="small" type="link" style={{padding: '0 2px', fontSize: '12px'}}
                        onClick={() => toggleCustom(index)} title="手动输入">
                    <EditOutlined/>
                </Button>
            )}
        </div>
    );
}

function renderRuleConfig(
    node: LogicFlowNode,
    onUpdate: (id: string, config: Record<string, unknown>) => void,
    graph?: { nodes: LogicFlowNode[]; edges: LogicFlowEdge[] },
    customLeftOperands?: Set<number>,
    toggleCustomLeft?: (idx: number) => void,
    customRightOperands?: Set<number>,
    toggleCustomRight?: (idx: number) => void,
): React.ReactNode {
    const config = node.config as any;
    const rules: ConditionRule[] = config.rules || [];
    const logicOperator = config.logicOperator || 'AND';

    const updateRule = (index: number, field: keyof ConditionRule, value: string) => {
        const newRules = rules.map((r: ConditionRule, i: number) =>
            i === index ? {...r, [field]: value} : r
        );
        onUpdate(node.id, {rules: newRules});
    };

    const addRule = () => {
        const newRules: ConditionRule[] = [
            ...rules,
            {leftOperand: "dataframe['close']", operator: '>', rightOperand: '0'},
        ];
        onUpdate(node.id, {rules: newRules});
    };

    const removeRule = (index: number) => {
        const newRules = rules.filter((_: ConditionRule, i: number) => i !== index);
        onUpdate(node.id, {rules: newRules});
    };

    return (
        <div>
            <Text type="secondary" style={{fontSize: '12px', marginBottom: '4px', display: 'block'}}>条件定义</Text>
            <div style={fieldStyle}>
                <Text type="secondary" style={{fontSize: '12px'}}>逻辑运算符</Text>
                <Select
                    size="small"
                    style={{width: '100%', ...solidInputStyle}}
                    value={logicOperator}
                    onChange={v => onUpdate(node.id, {logicOperator: v})}
                    options={[
                        {value: 'AND', label: 'AND（全部满足）'},
                        {value: 'OR', label: 'OR（任一满足）'},
                    ]}
                />
            </div>

            <Text type="secondary" style={{fontSize: '12px', margin: '8px 0', display: 'block'}}>
                条件规则
            </Text>

            {rules.map((rule: ConditionRule, i: number) => (
                <div key={i} style={ruleStyle}>
                    <div style={{display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap'}}>
                        {renderOperandCell(node.id, i, rule.leftOperand, graph, customLeftOperands, toggleCustomLeft, (v) => updateRule(i, 'leftOperand', v))}
                        <Select
                            size="small"
                            style={{width: '72px', ...solidInputStyle}}
                            value={rule.operator}
                            onChange={v => updateRule(i, 'operator', v)}
                            options={COMPARISON_OPERATORS}
                        />
                        {renderOperandCell(node.id, i, rule.rightOperand, graph, customRightOperands, toggleCustomRight, (v) => updateRule(i, 'rightOperand', v))}
                    </div>
                    <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined/>}
                        onClick={() => removeRule(i)}
                    />
                </div>
            ))}

            <Tooltip title="添加条件">
                <Button size="small" type="default" icon={<PlusOutlined/>} onClick={addRule} block
                        style={{marginTop: '4px'}}/>
            </Tooltip>

            <Divider style={{margin: '12px 0', borderColor: 'rgba(255,255,255,0.08)'}}/>

            <Text type="secondary" style={{fontSize: '12px', marginBottom: '4px', display: 'block'}}>交易动作</Text>
            <div style={fieldStyle}>
                <Select
                    size="small"
                    style={{width: '100%', ...solidInputStyle}}
                    value={config.action || 'enter_long'}
                    onChange={v => onUpdate(node.id, {action: v})}
                    options={ACTION_OPTIONS}
                />
            </div>
            {'none' !== config.action && (
                <div style={{...fieldStyle, marginTop: '8px'}}>
                    <Text type="secondary" style={{fontSize: '12px'}}>信号值</Text>
                    <Input
                        size="small"
                        value={config.signalValue || '1'}
                        onChange={e => onUpdate(node.id, {signalValue: e.target.value})}
                        style={solidInputStyle}
                    />
                </div>
            )}
        </div>
    );
}

// ========== 样式 ==========
const containerStyle: React.CSSProperties = {
    width: '280px',
    minWidth: '280px',
    padding: '16px 14px',
    borderLeft: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(12,17,32,0.92)',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
};

const emptyStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '120px',
};

const titleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
};

const fieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
};

const solidInputStyle: React.CSSProperties = {
    background: 'rgba(26,34,54,0.6)',
    borderColor: 'var(--pt-border)',
};

const codeStyle: React.CSSProperties = {
    padding: '10px',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(0,0,0,0.3)',
    color: 'var(--pt-text-primary)',
    fontSize: '11px',
    lineHeight: 1.5,
    fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    margin: 0,
    maxHeight: '200px',
};

const ruleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '4px',
    padding: '8px',
    marginBottom: '6px',
    borderRadius: '8px',
    border: '1px solid var(--pt-border)',
    background: 'rgba(26,34,54,0.4)',
};

export default NodeConfigPanel;
